import { useMemo, useState } from "react";
import type { PlanConfig, ReportConfigurationState } from "../types";

interface PlanRailProps {
    hasPlans: boolean;
    data: ReportConfigurationState;
    selectedPlan?: PlanConfig;
    clientsLoading: boolean;
    clientsError: string;
    onSelectPlan: (planId: string) => void;
}

export function PlanRail({ hasPlans, data, selectedPlan, clientsLoading, clientsError, onSelectPlan }: PlanRailProps) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [planSearch, setPlanSearch] = useState("");
    const filteredPlans = useMemo(() => {
        const query = planSearch.trim().toLowerCase();
        if (!query) return data.plans;
        return data.plans.filter((plan) => (
            plan.name.toLowerCase().includes(query)
            || (plan.clientName || "").toLowerCase().includes(query)
            || String(plan.clientId || "").includes(query)
        ));
    }, [data.plans, planSearch]);
    return (
        <aside className="rc-plan-rail">
            <div className="rc-plan-rail-header">
                <span><span className="material-icons md-16" aria-hidden="true">business</span> Health Plans</span>
                {hasPlans && <span className="rc-plan-count">{data.plans.length}</span>}
            </div>
            {clientsLoading && <p className="rc-hint">Loading Health Plans...</p>}
            {clientsError && <p className="rc-hint">{clientsError}</p>}
            {hasPlans && (
                <div className="rc-plan-context">
                    <div className="rc-plan-selected-card" aria-label="Selected health plan">
                        <span className="rc-plan-selected-mark">
                            <span className="material-icons md-18" aria-hidden="true">corporate_fare</span>
                        </span>
                        <span className="rc-plan-selected-copy">
                            <span className="rc-plan-selected-kicker">Selected Health Plan</span>
                            <span className="rc-plan-selected-title">{selectedPlan?.name || "Select Health Plan"}</span>
                            <span className="rc-plan-selected-subtitle">{selectedPlan?.clientName || "Choose the client context for this configuration"}</span>
                        </span>
                        <button
                            className="rc-plan-change-action"
                            type="button"
                            aria-haspopup="dialog"
                            aria-expanded={pickerOpen}
                            onClick={() => {
                                setPlanSearch("");
                                setPickerOpen(true);
                            }}
                        >
                            <span className="material-icons md-16" aria-hidden="true">swap_horiz</span>
                            Change Plan
                        </button>
                    </div>
                    {pickerOpen && (
                        <>
                            <button className="rc-plan-switcher-backdrop" type="button" aria-label="Close health plan switcher" onClick={() => setPickerOpen(false)} />
                            <aside className="rc-plan-switcher-drawer" role="dialog" aria-modal="true" aria-label="Switch Health Plan">
                                <div className="rc-plan-switcher-head">
                                    <span className="rc-plan-switcher-emblem">
                                        <span className="material-icons md-18" aria-hidden="true">corporate_fare</span>
                                    </span>
                                    <span>
                                        <strong>Health Plans</strong>
                                        <small>Select the client context for this report configuration.</small>
                                    </span>
                                    <span className="rc-plan-switcher-total">{data.plans.length}</span>
                                    <button className="rc-plan-switcher-close" type="button" aria-label="Close health plan switcher" onClick={() => setPickerOpen(false)}>
                                        <span className="material-icons md-18" aria-hidden="true">close</span>
                                    </button>
                                </div>
                                <div className="rc-plan-switcher-current">
                                    <span className="material-icons md-18" aria-hidden="true">verified</span>
                                    <span>
                                        <small>Current selection</small>
                                        <strong>{selectedPlan?.name || "No plan selected"}</strong>
                                    </span>
                                </div>
                                <label className="rc-plan-switcher-search">
                                    <span className="material-icons md-18" aria-hidden="true">manage_search</span>
                                    <input
                                        autoFocus
                                        value={planSearch}
                                        placeholder="Search by Client Name or Client ID..."
                                        onChange={(event) => setPlanSearch(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Escape") setPickerOpen(false);
                                        }}
                                    />
                                </label>
                                <div className="rc-plan-switcher-list">
                                    {filteredPlans.map((plan) => (
                                        <button
                                            className={plan.id === selectedPlan?.id ? "rc-plan-switcher-item active" : "rc-plan-switcher-item"}
                                            key={plan.id}
                                            type="button"
                                            onClick={() => {
                                                onSelectPlan(plan.id);
                                                setPickerOpen(false);
                                            }}
                                        >
                                            <span className="rc-plan-switcher-avatar">{plan.name.slice(0, 2).toUpperCase()}</span>
                                            <span className="rc-plan-switcher-text">
                                                <strong>{plan.name}</strong>
                                                <small>{plan.clientName || "ClientInfo"}</small>
                                            </span>
                                            {plan.id === selectedPlan?.id ? (
                                                <span className="rc-plan-switcher-state">
                                                    <span className="material-icons md-16" aria-hidden="true">check</span>
                                                </span>
                                            ) : (
                                                <span className="material-icons md-18 rc-plan-switcher-next" aria-hidden="true">chevron_right</span>
                                            )}
                                        </button>
                                    ))}
                                    {!filteredPlans.length && <p className="rc-hint">No matching health plans.</p>}
                                </div>
                            </aside>
                        </>
                    )}
                </div>
            )}
            <div className="rc-plan-list">
                {data.plans.map((plan) => (
                    <div key={plan.id} className={plan.id === selectedPlan?.id ? "rc-plan active" : "rc-plan"}>
                        <button className="rc-plan-name" title={plan.clientName} onClick={() => onSelectPlan(plan.id)}>
                            <span className="rc-plan-code">{plan.name}</span>
                            <span className="rc-plan-company">{plan.clientName || "ClientInfo"}</span>
                        </button>
                    </div>
                ))}
            </div>
        </aside>
    );
}
